// VOCAB-1 — A JOB STATUS CARRIES ITS MEANING SEPARATELY FROM ITS NAME.
//
// THE STANDING RULE THIS FILE EXISTS TO ENFORCE: BEHAVIOUR NEVER COMPARES
// AGAINST A WORD A BUSINESS CAN RENAME.
//
// Renaming a word is easy. SURVIVING a rename is not. Today, marking a job
// complete fires SIX automatic behaviours, and every one of them decides by
// comparing against the exact string 'completed':
//
//   1. fire_job_completed_event ................ inserts COMMISSION rows
//   2. tg_job_completed_owner_notification ..... enqueues the OWNER NOTIFICATION
//   3. review_request_enqueue_on_job_complete .. enqueues the REVIEW REQUEST
//   4. tg_bill_per_visit_invoice ............... raises the PER-VISIT INVOICE
//   5. stamp_job_status_timestamps ............. stamps completed_at
//   6. tg_workflow_jobs ........................ emits the job_completed EVENT
//
// (The audit said four. It is six - the timestamp stamper and the per-visit
// invoice were not counted. Read from pg_trigger on the live database, not
// from the migration files, because the database is the authority on what
// actually fires.)
//
// So the moment a status code becomes something a business can change, a
// technician's commission silently stops being paid. That is the failure this
// file is built to make impossible.
//
// THE PATTERN, AND IT IS NOT INVENTED HERE. Two things in this product already
// separate meaning from name, and this is modelled on them exactly:
//
//   pipeline_stages   name = the merchant's word, fully renamable
//                     stage_type ∈ active | win | loss = THE MEANING
//                     ConvertToCustomerModal asks stage_type = 'win'. It never
//                     asks whether the stage is called "Won".
//
//   canvass_dispositions
//                     label = the merchant's word, fully renamable
//                     counts_as_contact / is_positive / is_terminal = THE
//                     MEANING, five boolean flags in all
//                     canvass.ts todayStats counts by the FLAGS, so a merchant
//                     who renames "Not home" to "Nobody in" keeps their stats.
//
// Applied to job status: `code` is IDENTITY and never changes, `label` is
// DISPLAY and lives in vocabulary.ts, and the flags below are MEANING. Every
// behaviour asks a flag.
//
// SHAPED LIKE canvassDispositions.ts DELIBERATELY: system rows
// (sub_account_id null) visible to every tenant, merchant rows SHADOWING a
// system row with the same code, cached per sub-account, and a defensive
// fallback to the built-in set so a pre-migration database behaves exactly as
// it does today.
//
// NOTHING IN THIS WAVE CHANGES WHAT ANY OF THE SIX BEHAVIOURS DO. The flags
// are computed and asserted; converting the six call sites to read them is
// named in the report and left for the follow-up, because five of the six live
// in SECURITY DEFINER trigger functions and rewriting those is authorization
// DDL, which is print-only.

import { supabase } from './supabase'
import type { JobStatusCode } from './vocabulary'

/**
 * WHAT A JOB STATUS CAN MEAN.
 *
 * Every flag here was derived from a behaviour that exists today and compares
 * against a literal. None was invented to look thorough - the comment on each
 * names the caller it was read from.
 */
export interface JobStatusMeaning {
  /** Work is booked but not begun. Read by: tg_workflow_jobs INSERT branch
   *  (appointment_booked), UpcomingJobsWidget, the jobs_scheduled metric. */
  isScheduled: boolean
  /** Work is underway. Read by: stamp_job_status_timestamps (started_at),
   *  GlanceStrip's "booked work" count. */
  countsAsStarted: boolean
  /** THE WORK WAS DONE. The single most load-bearing flag in the product:
   *  all six completion behaviours above turn on this one. */
  countsAsComplete: boolean
  /** Called off. Read by: tg_workflow_jobs (appointment_cancelled +
   *  job_cancelled), stamp_job_status_timestamps (cancelled_at), portal-fetch,
   *  portal-job-ics. */
  countsAsCancelled: boolean
  /** The customer was not there. Read by: tg_workflow_jobs
   *  (appointment_no_show), automation-trigger-eval, TechView. */
  countsAsNoShow: boolean
  /** No further transition is expected. Read by: portal-fetch and
   *  portal-job-ics, which today ask `!== 'cancelled' && !== 'completed'`. */
  isTerminal: boolean
  /** The job's value counts toward money actually earned. Read by: the
   *  jobs_revenue metric and useHomeKpis, which today filter status =
   *  'completed' before summing. */
  countsAsRevenue: boolean
}

export interface JobStatusMeaningRow extends JobStatusMeaning {
  code: string
  /** Badge tone, TRANSCRIBED from STATUS_VARIANT in JobDetail.tsx. The tone
   *  vocabulary is the Badge component's own - neutral/accent/success/
   *  pending/danger. The first draft of this file INVENTED a different set
   *  of tone names, which is the exact mistake this wave is against; a
   *  cross-check test now reads JobDetail.tsx and fails on any drift. */
  badgeTone: 'neutral' | 'accent' | 'success' | 'pending' | 'danger'
  sortOrder: number
  isActive: boolean
  /** null/undefined = shared system row, uuid = merchant-owned row. */
  subAccountId?: string | null
  /** false for merchant-created rows. */
  isSystem?: boolean
}

const meaning = (
  isScheduled: boolean,
  countsAsStarted: boolean,
  countsAsComplete: boolean,
  countsAsCancelled: boolean,
  countsAsNoShow: boolean,
  isTerminal: boolean,
  countsAsRevenue: boolean,
): JobStatusMeaning => ({
  isScheduled,
  countsAsStarted,
  countsAsComplete,
  countsAsCancelled,
  countsAsNoShow,
  isTerminal,
  countsAsRevenue,
})

/**
 * THE BUILT-IN SEVEN, AND THESE FLAGS ARE A TRANSCRIPTION, NOT A DESIGN.
 *
 * Each row states what the CODE ALREADY MEANS to the behaviours listed above,
 * so that swapping a literal comparison for a flag read changes NOTHING. A
 * test asserts exactly that, code by code and behaviour by behaviour.
 *
 *                             sched  start  compl  cancl  noshow  term   revenue
 */
export const BUILTIN_JOB_STATUS_MEANINGS: Record<JobStatusCode, JobStatusMeaning> = {
  scheduled: meaning(true, false, false, false, false, false, false),
  // On the way is still "booked and not begun" to every behaviour that asks:
  // no trigger treats en_route as started, and nothing stamps started_at for
  // it. Transcribed as it is, NOT as it arguably ought to be.
  en_route: meaning(true, false, false, false, false, false, false),
  in_progress: meaning(false, true, false, false, false, false, false),
  completed: meaning(false, true, true, false, false, true, true),
  cancelled: meaning(false, false, false, true, false, true, false),
  // A no-show is terminal - the visit will not happen - but it is NOT a
  // cancellation: tg_workflow_jobs emits a different event for each, and a
  // business that treats them the same loses the distinction its automations
  // are built on.
  no_show: meaning(false, false, false, false, true, true, false),
  // Rescheduled goes BACK to booked. Not terminal, and no money.
  rescheduled: meaning(true, false, false, false, false, false, false),
}

/** Badge tones, TRANSCRIBED CHARACTER FOR CHARACTER from STATUS_VARIANT in
 *  src/pages/dashboard/JobDetail.tsx - so the tone survives a rename too.
 *  A test reads that file and fails if the two ever disagree. */
export const BUILTIN_JOB_STATUS_TONES: Record<JobStatusCode, JobStatusMeaningRow['badgeTone']> = {
  scheduled: 'neutral',
  en_route: 'accent',
  in_progress: 'accent',
  completed: 'success',
  cancelled: 'danger',
  no_show: 'danger',
  rescheduled: 'pending',
}

/** Workflow order, the order the settings editor and any status list renders. */
export const JOB_STATUS_ORDER: JobStatusCode[] = [
  'scheduled',
  'en_route',
  'in_progress',
  'completed',
  'rescheduled',
  'no_show',
  'cancelled',
]

/** The built-in registry as rows, identical in shape to what the table stores. */
export const BUILTIN_JOB_STATUS_ROWS: JobStatusMeaningRow[] = JOB_STATUS_ORDER.map((code, i) => ({
  code,
  ...BUILTIN_JOB_STATUS_MEANINGS[code],
  badgeTone: BUILTIN_JOB_STATUS_TONES[code],
  sortOrder: i,
  isActive: true,
  subAccountId: null,
  isSystem: true,
}))

// ─── Loading ───────────────────────────────────────────────────────────

/** A DB row, snake_case, mirroring the columns the migration creates. */
export interface JobStatusMeaningDbRow {
  code: string
  is_scheduled: boolean
  counts_as_started: boolean
  counts_as_complete: boolean
  counts_as_cancelled: boolean
  counts_as_no_show: boolean
  is_terminal: boolean
  counts_as_revenue: boolean
  badge_tone: string
  sort_order: number
  is_active: boolean
  sub_account_id?: string | null
  is_system?: boolean
}

const TONES = new Set(['neutral', 'accent', 'success', 'pending', 'danger'])

function fromDb(r: JobStatusMeaningDbRow): JobStatusMeaningRow {
  return {
    code: r.code,
    isScheduled: r.is_scheduled,
    countsAsStarted: r.counts_as_started,
    countsAsComplete: r.counts_as_complete,
    countsAsCancelled: r.counts_as_cancelled,
    countsAsNoShow: r.counts_as_no_show,
    isTerminal: r.is_terminal,
    countsAsRevenue: r.counts_as_revenue,
    badgeTone: TONES.has(r.badge_tone)
      ? (r.badge_tone as JobStatusMeaningRow['badgeTone'])
      : 'neutral',
    sortOrder: r.sort_order,
    isActive: r.is_active,
    subAccountId: r.sub_account_id ?? null,
    isSystem: r.is_system ?? false,
  }
}

export interface LoadedJobStatusMeanings {
  byCode: Map<string, JobStatusMeaningRow>
  /** Active rows in sort order — for any status picker. */
  rows: JobStatusMeaningRow[]
}

/**
 * Pure merge. A MERCHANT ROW SHADOWS A SYSTEM ROW WITH THE SAME CODE, exactly
 * as canvass dispositions do. Exported for tests.
 */
export function buildLoadedJobStatusMeanings(
  rows: JobStatusMeaningDbRow[],
): LoadedJobStatusMeanings {
  const byCode = new Map<string, JobStatusMeaningRow>()
  for (const raw of rows) {
    const r = fromDb(raw)
    const existing = byCode.get(r.code)
    const rowIsMerchant = r.subAccountId != null
    const existingIsMerchant = existing != null && existing.subAccountId != null
    if (!existing || (rowIsMerchant && !existingIsMerchant)) byCode.set(r.code, r)
  }
  const active = [...byCode.values()]
    .filter((r) => r.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))
  return { byCode, rows: active }
}

/** The built-in set, in the loaded shape. The pre-migration answer, and the
 *  answer whenever a read fails. */
export function builtinLoadedJobStatusMeanings(): LoadedJobStatusMeanings {
  const byCode = new Map<string, JobStatusMeaningRow>()
  for (const r of BUILTIN_JOB_STATUS_ROWS) byCode.set(r.code, r)
  return { byCode, rows: [...BUILTIN_JOB_STATUS_ROWS] }
}

/**
 * WHAT A STATUS MEANS. The single function every behaviour should ask.
 *
 * An UNKNOWN code - a status this deployment has never heard of - returns a
 * meaning with EVERY FLAG FALSE rather than throwing or guessing. That is the
 * conservative answer: an unknown status does not pay a commission, does not
 * raise an invoice and does not count as revenue. Silence, not a wrong action.
 */
export const UNKNOWN_MEANING: JobStatusMeaning = meaning(
  false,
  false,
  false,
  false,
  false,
  false,
  false,
)

export function meaningOf(
  loaded: LoadedJobStatusMeanings,
  status: string | null | undefined,
): JobStatusMeaning {
  if (!status) return UNKNOWN_MEANING
  const row = loaded.byCode.get(status)
  if (row) return row
  return UNKNOWN_MEANING
}

// ─── The three states, and the tenant cache ────────────────────────────

export type JobStatusMeaningsState =
  | { state: 'loading' }
  | { state: 'failed'; reason: string }
  | { state: 'loaded'; loaded: LoadedJobStatusMeanings }

const cache = new Map<string, LoadedJobStatusMeanings>()
const inflight = new Map<string, Promise<JobStatusMeaningsState>>()

/** Test / post-save seam: drop every cached tenant's meanings. */
export function resetJobStatusMeaningsCache(): void {
  cache.clear()
  inflight.clear()
}

/**
 * Load one business's status meanings.
 *
 * A MISSING TABLE IS NOT A FAILURE. Before the migration is applied the table
 * does not exist, and the honest answer is "this business has configured
 * nothing", which resolves to the built-in seven - byte-identical to today.
 * Only a REAL error is a failure, and a failure is never cached.
 */
export async function loadJobStatusMeanings(
  subAccountId?: string | null,
): Promise<JobStatusMeaningsState> {
  if (!subAccountId) return { state: 'loaded', loaded: builtinLoadedJobStatusMeanings() }
  const hit = cache.get(subAccountId)
  if (hit) return { state: 'loaded', loaded: hit }
  const pending = inflight.get(subAccountId)
  if (pending) return pending
  const promise = (async (): Promise<JobStatusMeaningsState> => {
    try {
      // Boundary cast: job_status_meanings is not in the generated types yet.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = supabase as any
      const res = await sb
        .from('job_status_meanings')
        .select(
          'code,is_scheduled,counts_as_started,counts_as_complete,counts_as_cancelled,' +
            'counts_as_no_show,is_terminal,counts_as_revenue,badge_tone,sort_order,' +
            'is_active,sub_account_id,is_system',
        )
        .or(`sub_account_id.is.null,sub_account_id.eq.${subAccountId}`)
      if (res.error) {
        // The table not existing yet is "configured nothing", not "could not
        // ask" - the same distinction PLATFORM-1 draws for its nav table.
        if (res.error.code === '42P01' || res.error.code === 'PGRST205') {
          const builtin = builtinLoadedJobStatusMeanings()
          cache.set(subAccountId, builtin)
          return { state: 'loaded', loaded: builtin }
        }
        return { state: 'failed', reason: res.error.message ?? 'The read did not succeed.' }
      }
      const rows = (res.data ?? []) as JobStatusMeaningDbRow[]
      // Read successfully and there is nothing: the built-in seven.
      const loaded = rows.length
        ? buildLoadedJobStatusMeanings(rows)
        : builtinLoadedJobStatusMeanings()
      cache.set(subAccountId, loaded)
      return { state: 'loaded', loaded }
    } catch (e) {
      return {
        state: 'failed',
        reason: e instanceof Error ? e.message : 'The read did not succeed.',
      }
    } finally {
      inflight.delete(subAccountId)
    }
  })()
  inflight.set(subAccountId, promise)
  return promise
}

/** Resolve a state to something renderable, with a warning when it failed. */
export function resolveJobStatusMeanings(state: JobStatusMeaningsState): {
  loaded: LoadedJobStatusMeanings
  warning: string | null
} {
  if (state.state === 'loaded') return { loaded: state.loaded, warning: null }
  if (state.state === 'failed') {
    return {
      loaded: builtinLoadedJobStatusMeanings(),
      warning: `We could not read what your statuses mean, so the standard meanings are in use. ${state.reason}`,
    }
  }
  return { loaded: builtinLoadedJobStatusMeanings(), warning: null }
}
